import * as i0 from '@angular/core';
import { isDevMode, Component, ChangeDetectionStrategy, ChangeDetectorRef, SimpleChange, Injectable, EventEmitter, Directive, Input, Output, Pipe, NgModule } from '@angular/core';
import { Subject } from 'rxjs';

const PRIVATE_PREFIX = '__ngxOnChanges_';
function hasOnChangesHook(component) {
    return component && hasProperty(component, 'ngOnChanges');
}
function hasDoCheckHook(component) {
    return component && hasProperty(component, 'ngDoCheck');
}
function hasOnInitHook(component) {
    return component && hasProperty(component, 'ngOnInit');
}
function createComponentRef(componentType, viewContainerRef, componentFactoryResolver) {
    const componentFactory = componentFactoryResolver.resolveComponentFactory(componentType);
    return viewContainerRef.createComponent(componentFactory, viewContainerRef.length);
}
function runOnChangesHook(context) {
    const simpleChanges = context[PRIVATE_PREFIX];
    if (simpleChanges != null && hasOnChangesHook(context)) {
        context.ngOnChanges(simpleChanges);
    }
    context[PRIVATE_PREFIX] = null;
}
function hasProperty(context, name) {
    if (name in context) {
        return true;
    }
    const prototype = Object.getPrototypeOf(context);
    if (prototype) {
        return hasProperty(prototype, name);
    }
    return false;
}
function getPropertyDescriptor(context, name) {
    const descriptor = Object.getOwnPropertyDescriptor(context, name);
    if (descriptor) {
        return Object.getOwnPropertyDescriptor(context, name);
    }
    const prototype = Object.getPrototypeOf(context);
    if (prototype) {
        return getPropertyDescriptor(prototype, name);
    }
    return void 0;
}
function deletePropertyDescriptor(context, name) {
    const descriptor = Object.getOwnPropertyDescriptor(context, name);
    if (descriptor) {
        delete context[name];
    }
    const prototype = Object.getPrototypeOf(context);
    if (prototype) {
        return deletePropertyDescriptor(prototype, name);
    }
}
// {propName: "insidePropName", templateName: "outsidePropName"}
function toPropertyDef(context, dynamicContext, hostContext) {
    return (property) => ({
        context: context,
        dynamicContext: dynamicContext,
        hostContext: hostContext,
        insidePropName: property.propName,
        outsidePropName: property.templateName,
    });
}
const PRIVATE_CONTEXT_PREFIX = '__ngxContext__';

const PRIVATE_HOST_INPUT_ADAPTER = PRIVATE_CONTEXT_PREFIX + 'HOST_INPUT_ADAPTER';
class HostInputAdapter {
    constructor(host, name) {
        this.host = host;
        this.name = name;
        this.disposed = false;
        if (PRIVATE_HOST_INPUT_ADAPTER + name in host) {
            return host[PRIVATE_HOST_INPUT_ADAPTER + name];
        }
        host[PRIVATE_HOST_INPUT_ADAPTER + name] = this;
        this.changes = new Subject();
        this.defaultDescriptor = getPropertyDescriptor(host, name);
        if (this.defaultDescriptor && this.defaultDescriptor.get && !this.defaultDescriptor.set) {
            if (isDevMode()) {
                const constructorName = host.constructor.name;
                console.log(`You should use get and set descriptors both with dynamic components:
ERROR: not found '${name}' input, it has setter only, please add getter!

  class ${constructorName} {

    // Please add that 👇
    get ${name}() { ... }

    set ${name}() { ... }

  }`);
            }
        }
        this.refCount = 0;
        const defaultValue = host[name];
        Object.defineProperty(host, name, {
            get: () => {
                if (this.defaultDescriptor && this.defaultDescriptor.get) {
                    return this.defaultDescriptor.get.call(host);
                }
                return this.value;
            },
            set: (value) => {
                if (this.defaultDescriptor && this.defaultDescriptor.set) {
                    this.defaultDescriptor.set.call(host, value);
                }
                this.value = value;
                this.changes.next(value);
            },
            configurable: true,
        });
        if (typeof defaultValue !== 'undefined') {
            host[name] = defaultValue;
        }
    }
    attach() {
        this.refCount++;
    }
    detach() {
        this.refCount--;
        if (this.refCount <= 0) {
            this.dispose();
        }
    }
    dispose() {
        const defaultValue = this.host[this.name];
        this.disposed = true;
        this.changes.complete();
        delete this.host[PRIVATE_HOST_INPUT_ADAPTER + this.name];
        if (this.defaultDescriptor) {
            if (this.defaultDescriptor.writable) {
                this.defaultDescriptor.value = defaultValue;
            }
            Object.defineProperty(this.host, this.name, this.defaultDescriptor);
            if (this.defaultDescriptor.set) {
                this.host[this.name] = defaultValue;
            }
        }
        else {
            delete this.host[this.name];
            this.host[this.name] = defaultValue;
        }
    }
}

const PRIVATE_HOST_ADAPTER = PRIVATE_CONTEXT_PREFIX + 'HOST_ADAPTER';
class HostAdapter {
    constructor(host) {
        this.host = host;
        if (PRIVATE_HOST_ADAPTER in host) {
            return host[PRIVATE_HOST_ADAPTER];
        }
        this.inputs = new Map();
        this.state = {};
        this.refCount = 0;
        host[PRIVATE_HOST_ADAPTER] = this;
    }
    attach() {
        this.refCount++;
    }
    attachInput(propertyDef) {
        const adapter = new HostInputAdapter(this.host, propertyDef.outsidePropName);
        adapter.attach();
        this.inputs.set(propertyDef.outsidePropName, adapter);
        return { ...propertyDef, defaultDescriptor: adapter.defaultDescriptor };
    }
    getInputAdapter(bindingDef) {
        return this.inputs.get(bindingDef.outsidePropName);
    }
    detachInput(bindingDef) {
        const adapter = this.inputs.get(bindingDef.outsidePropName);
        adapter.detach();
        if (adapter.disposed) {
            this.inputs.delete(bindingDef.outsidePropName);
        }
    }
    detach() {
        this.refCount--;
        if (this.refCount <= 0) {
            this.dispose();
        }
    }
    dispose() {
        delete this.host[PRIVATE_HOST_ADAPTER];
    }
}

var _a, _b, _c;
const lifeCycleComponentSymbol = Symbol('Life Cycle Component');
function isLifecycleComponent(component) {
    return component && component[lifeCycleComponentSymbol];
}
class OnInitOnlyComponent {
    constructor() {
        this[_a] = true;
    }
    ngOnInit() {
        if (this.context) {
            runOnChangesHook(this.context);
        }
    }
    attach(context, changeDetectorRef) {
        this.context = context;
        this.changeDetectorRef = changeDetectorRef;
    }
}
_a = lifeCycleComponentSymbol;
/** @nocollapse */ OnInitOnlyComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: OnInitOnlyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ OnInitOnlyComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.2", type: OnInitOnlyComponent, selector: "ngx-component-outlet-on-init-only", ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: OnInitOnlyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ngx-component-outlet-on-init-only',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });
class DoCheckOnlyComponent {
    constructor() {
        this[_b] = true;
    }
    ngDoCheck() {
        if (this.context) {
            runOnChangesHook(this.context);
        }
        if (this.changeDetectorRef) {
            this.changeDetectorRef.markForCheck();
        }
    }
    attach(context, changeDetectorRef) {
        this.context = context;
        this.changeDetectorRef = changeDetectorRef;
    }
}
_b = lifeCycleComponentSymbol;
/** @nocollapse */ DoCheckOnlyComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: DoCheckOnlyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ DoCheckOnlyComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.2", type: DoCheckOnlyComponent, selector: "ngx-component-outlet-do-check-only", ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: DoCheckOnlyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ngx-component-outlet-do-check-only',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });
class OnInitAndDoCheckComponent {
    constructor() {
        this[_c] = true;
    }
    ngOnInit() {
        if (this.context) {
            runOnChangesHook(this.context);
        }
    }
    ngDoCheck() {
        if (this.context) {
            runOnChangesHook(this.context);
        }
        if (this.changeDetectorRef) {
            this.changeDetectorRef.markForCheck();
        }
    }
    attach(context, changeDetectorRef) {
        this.context = context;
        this.changeDetectorRef = changeDetectorRef;
    }
}
_c = lifeCycleComponentSymbol;
/** @nocollapse */ OnInitAndDoCheckComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: OnInitAndDoCheckComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ OnInitAndDoCheckComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.2", type: OnInitAndDoCheckComponent, selector: "ngx-component-outlet-on-init-do-check", ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: OnInitAndDoCheckComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ngx-component-outlet-on-init-do-check',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });

var LifecycleStrategyType;
(function (LifecycleStrategyType) {
    LifecycleStrategyType[LifecycleStrategyType["Default"] = 0] = "Default";
    LifecycleStrategyType[LifecycleStrategyType["OnInitOnly"] = 1] = "OnInitOnly";
    LifecycleStrategyType[LifecycleStrategyType["DoCheckOnly"] = 2] = "DoCheckOnly";
    LifecycleStrategyType[LifecycleStrategyType["OnInitAndDoCheck"] = 3] = "OnInitAndDoCheck";
})(LifecycleStrategyType || (LifecycleStrategyType = {}));
const STRATEGY_CONFIG = {
    [LifecycleStrategyType.Default]: {},
    [LifecycleStrategyType.OnInitOnly]: {
        componentType: OnInitOnlyComponent,
        useOnInitComponent: true,
    },
    [LifecycleStrategyType.DoCheckOnly]: {
        componentType: DoCheckOnlyComponent,
        useDoCheckComponent: true,
    },
    [LifecycleStrategyType.OnInitAndDoCheck]: {
        componentType: OnInitAndDoCheckComponent,
        useOnInitComponent: true,
        useDoCheckComponent: true,
    },
};
const FEATURE_COMPONENTS_DISABLE = {
    onInitComponentRef: null,
    doCheckComponentRef: null,
};
function resolveLifecycleStrategy(component) {
    const hasNgOnInit = hasProperty(component.prototype, 'ngOnInit');
    const hasNgDoCheck = hasProperty(component.prototype, 'ngDoCheck');
    const hasNgOnChanges = hasProperty(component.prototype, 'ngOnChanges');
    let type;
    if (hasNgOnChanges && !hasNgOnInit && !hasNgDoCheck) {
        type = LifecycleStrategyType.OnInitAndDoCheck;
    }
    else if (hasNgOnChanges && !hasNgOnInit) {
        type = LifecycleStrategyType.OnInitOnly;
    }
    else if (!hasNgDoCheck) {
        type = LifecycleStrategyType.DoCheckOnly;
    }
    else {
        type = LifecycleStrategyType.Default;
    }
    const USE_ONLY_INIT_AND_DO_CHECK_STRATEGY_FOR_IVY = LifecycleStrategyType.OnInitAndDoCheck;
    return STRATEGY_CONFIG[USE_ONLY_INIT_AND_DO_CHECK_STRATEGY_FOR_IVY];
}
function createLifecycleComponents(componentType, viewContainerRef, componentFactoryResolver) {
    const config = resolveLifecycleStrategy(componentType);
    if (!config.componentType) {
        return FEATURE_COMPONENTS_DISABLE;
    }
    const lifecycleComponentRef = createComponentRef(config.componentType, viewContainerRef, componentFactoryResolver);
    return {
        onInitComponentRef: config.useOnInitComponent ? lifecycleComponentRef : null,
        doCheckComponentRef: config.useDoCheckComponent ? lifecycleComponentRef : null,
    };
}
function attachLifecycle(componentRef, viewContainerRef, componentFactoryResolver) {
    const component = componentRef.instance;
    const componentType = component.constructor;
    let components = createLifecycleComponents(componentType, viewContainerRef, componentFactoryResolver);
    components = {
        onInitComponentRef: components.onInitComponentRef || componentRef,
        doCheckComponentRef: components.doCheckComponentRef || componentRef,
    };
    if (hasOnChangesHook(component)) {
        if (isLifecycleComponent(components.onInitComponentRef.instance)) {
            components.onInitComponentRef.instance.attach(component, componentRef.changeDetectorRef);
        }
        else {
            console.warn('todo: add for OnInit on dynamic component');
        }
        if (isLifecycleComponent(components.doCheckComponentRef.instance)) {
            components.doCheckComponentRef.instance.attach(component, componentRef.changeDetectorRef);
        }
        else {
            console.warn('todo: add for DoCheck on dynamic component');
        }
    }
    else {
        if (isLifecycleComponent(components.doCheckComponentRef.instance)) {
            components.doCheckComponentRef.instance.attach(component, componentRef.changeDetectorRef);
        }
        else {
            console.warn('todo: add for DoCheck on dynamic component');
        }
    }
    return () => {
        if (isLifecycleComponent(components.doCheckComponentRef.instance)) {
            components.doCheckComponentRef.destroy();
        }
        if (isLifecycleComponent(components.onInitComponentRef.instance)) {
            components.onInitComponentRef.destroy();
        }
    };
}

class NgxComponentOutletAdapterRef {
    constructor(config, viewContainerRef, componentFactoryResolver) {
        this.viewContainerRef = viewContainerRef;
        this.componentFactoryResolver = componentFactoryResolver;
        this.context = {};
        this.attachedInputs = [];
        this.attachedOutputs = [];
        this.propertyDefs = [];
        this.bindingDefs = [];
        this.componentFactory = config.componentFactory;
        this.componentRef = config.componentRef;
        this.host = config.host;
        this.changeDetectorRef = this.componentRef.injector.get(ChangeDetectorRef, this.componentRef.changeDetectorRef);
        this.propertyDefs = this.componentFactory.inputs.map(toPropertyDef(this.context, this.componentRef.instance, this.host));
        this.attachHost();
        this.attachInputs();
        this.attachLifecycle();
        this.attachOutputs();
    }
    dispose() {
        this.disposeOutputs();
        this.disposeInputs();
        this.detachHost();
        if (this.componentRef) {
            this.componentRef.destroy();
            this.componentRef = null;
        }
        if (this.detachLifecycle) {
            this.detachLifecycle();
            this.detachLifecycle = null;
        }
    }
    updateContext(context) {
        const contextProps = context ? Object.keys(context) : [];
        for (const contextPropName of contextProps) {
            const bindingDef = this.getBindingDef(contextPropName);
            if (bindingDef) {
                this.detachHostInput(bindingDef);
            }
            const propertyDef = this.getPropertyDef(contextPropName);
            if (propertyDef && this.context[propertyDef.outsidePropName] !== context[contextPropName]) {
                this.context[propertyDef.outsidePropName] = context[contextPropName];
            }
        }
        const unattachedProps = this.propertyDefs.filter(propertyDef => !(contextProps.indexOf(propertyDef.outsidePropName) > -1 ||
            this.getBindingDef(propertyDef.outsidePropName)));
        for (const propertyDef of unattachedProps) {
            const bindingDef = this.attachHostInput(propertyDef);
            this.attachInput(bindingDef);
        }
    }
    getPropertyDef(outsidePropName) {
        return this.propertyDefs.find(_ => _.outsidePropName === outsidePropName);
    }
    getBindingDef(outsidePropName) {
        return this.bindingDefs.find(_ => _.outsidePropName === outsidePropName);
    }
    attachHost() {
        this.hostAdapter = new HostAdapter(this.host);
        this.hostAdapter.attach();
    }
    detachHost() {
        this.hostAdapter.detach();
        this.hostAdapter = null;
    }
    attachHostInput(propertyDef) {
        const bindingDef = this.hostAdapter.attachInput(propertyDef);
        this.bindingDefs.push(bindingDef);
        return bindingDef;
    }
    detachHostInput(bindingDef) {
        this.hostAdapter.detachInput(bindingDef);
        this.bindingDefs = this.bindingDefs.filter(_ => _ !== bindingDef);
    }
    attachInputs() {
        this.bindingDefs = [];
        for (const propertyDef of this.propertyDefs) {
            const bindingDef = this.attachHostInput(propertyDef);
            this.attachContextPropertyToComponentInput(bindingDef);
            this.attachInput(bindingDef);
        }
    }
    attachInput(bindingDef) {
        const host = this.host;
        const hostAdapter = this.hostAdapter;
        const context = this.context;
        const defaultValue = host[bindingDef.outsidePropName];
        if (typeof defaultValue !== 'undefined') {
            context[bindingDef.outsidePropName] = defaultValue;
        }
        const subscription = hostAdapter.getInputAdapter(bindingDef).changes.subscribe(value => {
            context[bindingDef.outsidePropName] = value;
        });
        this.attachedInputs.push(subscription);
    }
    attachContextPropertyToComponentInput(bindingDef) {
        const { insidePropName, outsidePropName, defaultDescriptor } = bindingDef;
        Object.defineProperty(bindingDef.context, outsidePropName, {
            get: () => {
                if (defaultDescriptor && defaultDescriptor.get) {
                    return defaultDescriptor.get.call(bindingDef.context);
                }
                else {
                    return bindingDef.dynamicContext[insidePropName];
                }
            },
            set: (value) => {
                if (bindingDef.dynamicContext[insidePropName] === value) {
                    return void 0;
                }
                let simpleChanges = bindingDef.dynamicContext[PRIVATE_PREFIX];
                if (simpleChanges == null) {
                    simpleChanges = bindingDef.dynamicContext[PRIVATE_PREFIX] = {};
                }
                const isFirstChange = !bindingDef.dynamicContext[`${PRIVATE_PREFIX}_${outsidePropName}`];
                bindingDef.dynamicContext[`${PRIVATE_PREFIX}_${outsidePropName}`] = true;
                simpleChanges[outsidePropName] = new SimpleChange(bindingDef.dynamicContext[insidePropName], value, isFirstChange);
                if (defaultDescriptor && defaultDescriptor.set) {
                    defaultDescriptor.set.call(bindingDef.hostContext, value);
                }
                try {
                    bindingDef.dynamicContext[insidePropName] = value;
                }
                catch (e) {
                    // Todo: add more debug
                    if (isDevMode()) {
                        const constructorName = bindingDef.dynamicContext.constructor.name;
                        console.log(`You should use get and set descriptors both with dynamic components:
ERROR: not found '${insidePropName}' input, it has getter only, please add setter!

  class ${constructorName} {

    get ${insidePropName}() { ... }

    // Please add that 👇
    set ${insidePropName}() { ... }

  }`);
                        console.error(e);
                    }
                }
                this.changeDetectorRef.markForCheck();
            },
        });
    }
    attachLifecycle() {
        this.detachLifecycle = attachLifecycle(this.componentRef, this.viewContainerRef, this.componentFactoryResolver);
    }
    disposeInputs() {
        for (const bindingDef of this.bindingDefs) {
            this.detachHostInput(bindingDef);
        }
        for (const subscription of this.attachedInputs) {
            subscription.unsubscribe();
        }
        this.attachedInputs.splice(0);
    }
    attachOutputs() {
        const propertyDefs = this.componentFactory.outputs.map(toPropertyDef(this.context, this.componentRef.instance, this.host));
        for (const propertyDef of propertyDefs) {
            if (propertyDef.outsidePropName in this.host) {
                const subscription = this.componentRef.instance[propertyDef.insidePropName].subscribe(this.host[propertyDef.outsidePropName]);
                this.attachedOutputs.push(subscription);
            }
        }
    }
    disposeOutputs() {
        for (const subscription of this.attachedOutputs) {
            subscription.unsubscribe();
        }
        this.attachedOutputs.splice(0);
    }
}

/**
 * @deprecated
 */
class NgxComponentOutletAdapterBuilder {
    create(componentType, viewContainerRef, injector, projectableNodes, host, componentFactoryResolver) {
        const componentFactory = componentFactoryResolver.resolveComponentFactory(componentType);
        const componentRef = componentFactory.create(injector, projectableNodes);
        const adapterRef = new NgxComponentOutletAdapterRef({
            componentFactory,
            componentRef,
            host,
        }, viewContainerRef, componentFactoryResolver);
        viewContainerRef.insert(componentRef.hostView, viewContainerRef.length);
        return adapterRef;
    }
}
/** @nocollapse */ NgxComponentOutletAdapterBuilder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: NgxComponentOutletAdapterBuilder, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ NgxComponentOutletAdapterBuilder.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: NgxComponentOutletAdapterBuilder });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: NgxComponentOutletAdapterBuilder, decorators: [{
            type: Injectable
        }] });

class NgxComponentOutletDirective {
    constructor(_componentFactoryResolver, viewContainerRef, changeDetectorRef, builder) {
        this._componentFactoryResolver = _componentFactoryResolver;
        this.viewContainerRef = viewContainerRef;
        this.changeDetectorRef = changeDetectorRef;
        this.builder = builder;
        this.ngxComponentOutletActivate = new EventEmitter();
        this.ngxComponentOutletDeactivate = new EventEmitter();
    }
    get componentFactoryResolver() {
        return this._ngModuleRef
            ? this._ngModuleRef.componentFactoryResolver
            : this._componentFactoryResolver;
    }
    get host() {
        if (this.cached) {
            return this.cached;
        }
        const { context } = this.changeDetectorRef;
        return (this.cached = context);
    }
    get injector() {
        return this.ngxComponentOutletInjector || this.viewContainerRef.injector;
    }
    ngOnChanges(changes) {
        if (changes.ngxComponentOutlet || changes.ngxComponentOutletInjector) {
            if (changes.ngxComponentOutletNgModuleFactory) {
                this.destroyNgModuleRef();
                this.createNgModuleRef();
            }
            this.destroyAdapterRef();
            this.createAdapterRef();
        }
        if (changes.ngxComponentOutletContext) {
            this.applyContext();
        }
    }
    applyContext() {
        if (this._adapterRef) {
            this._adapterRef.updateContext(this.ngxComponentOutletContext);
        }
    }
    ngOnDestroy() {
        this.destroyAdapterRef();
        this.destroyNgModuleRef();
    }
    createAdapterRef() {
        if (this.ngxComponentOutlet) {
            this._adapterRef = this.builder.create(this.ngxComponentOutlet, this.viewContainerRef, this.injector, this.ngxComponentOutletContent, this.host, this.componentFactoryResolver);
            if (this.ngxComponentOutletContext) {
                this.applyContext();
            }
            this.ngxComponentOutletActivate.emit(this._adapterRef.componentRef.instance);
        }
    }
    destroyAdapterRef() {
        if (this._adapterRef) {
            this.ngxComponentOutletDeactivate.emit(this._adapterRef.componentRef.instance);
            this._adapterRef.dispose();
            this._adapterRef = null;
        }
    }
    createNgModuleRef() {
        if (this.ngxComponentOutletNgModuleFactory) {
            this._ngModuleRef = this.ngxComponentOutletNgModuleFactory.create(this.injector);
        }
    }
    destroyNgModuleRef() {
        if (this._ngModuleRef) {
            this._ngModuleRef.destroy();
            this._ngModuleRef = null;
        }
    }
}
/** @nocollapse */ NgxComponentOutletDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: NgxComponentOutletDirective, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.ViewContainerRef }, { token: i0.ChangeDetectorRef }, { token: NgxComponentOutletAdapterBuilder }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ NgxComponentOutletDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.2", type: NgxComponentOutletDirective, selector: "[ngxComponentOutlet]", inputs: { ngxComponentOutlet: "ngxComponentOutlet", ngxComponentOutletInjector: "ngxComponentOutletInjector", ngxComponentOutletContent: "ngxComponentOutletContent", ngxComponentOutletContext: "ngxComponentOutletContext", ngxComponentOutletNgModuleFactory: "ngxComponentOutletNgModuleFactory" }, outputs: { ngxComponentOutletActivate: "ngxComponentOutletActivate", ngxComponentOutletDeactivate: "ngxComponentOutletDeactivate" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: NgxComponentOutletDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[ngxComponentOutlet]' }]
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }, { type: i0.ViewContainerRef }, { type: i0.ChangeDetectorRef }, { type: NgxComponentOutletAdapterBuilder }]; }, propDecorators: { ngxComponentOutlet: [{
                type: Input
            }], ngxComponentOutletInjector: [{
                type: Input
            }], ngxComponentOutletContent: [{
                type: Input
            }], ngxComponentOutletContext: [{
                type: Input
            }], ngxComponentOutletNgModuleFactory: [{
                type: Input
            }], ngxComponentOutletActivate: [{
                type: Output
            }], ngxComponentOutletDeactivate: [{
                type: Output
            }] } });

class NgxComponentOutletResolvePipe {
    transform(resolver, value) {
        return resolver && resolver.resolve(value);
    }
}
/** @nocollapse */ NgxComponentOutletResolvePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: NgxComponentOutletResolvePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
/** @nocollapse */ NgxComponentOutletResolvePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.2", ngImport: i0, type: NgxComponentOutletResolvePipe, name: "resolve" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: NgxComponentOutletResolvePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'resolve', pure: true }]
        }] });

class NgxdModule {
}
/** @nocollapse */ NgxdModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: NgxdModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ NgxdModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.2", ngImport: i0, type: NgxdModule, declarations: [NgxComponentOutletDirective,
        NgxComponentOutletResolvePipe,
        OnInitOnlyComponent,
        DoCheckOnlyComponent,
        OnInitAndDoCheckComponent], exports: [NgxComponentOutletDirective, NgxComponentOutletResolvePipe] });
/** @nocollapse */ NgxdModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: NgxdModule, providers: [NgxComponentOutletAdapterBuilder] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: NgxdModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        NgxComponentOutletDirective,
                        NgxComponentOutletResolvePipe,
                        OnInitOnlyComponent,
                        DoCheckOnlyComponent,
                        OnInitAndDoCheckComponent,
                    ],
                    entryComponents: [OnInitOnlyComponent, DoCheckOnlyComponent, OnInitAndDoCheckComponent],
                    exports: [NgxComponentOutletDirective, NgxComponentOutletResolvePipe],
                    providers: [NgxComponentOutletAdapterBuilder],
                }]
        }] });

class NgxdResolver {
    constructor(providers = []) {
        this.config = providers.reduce((config, provider) => config.set(provider.type, provider.component), new Map());
    }
    resolve(type) {
        if (type && type.constructor) {
            return this.config.get(type.constructor) || this.config.get(type) || null;
        }
        return this.config.get(type) || null;
    }
}

class NgxdProvider {
}
function Dynamic({ token, } = {}) {
    return function (provider) {
        return { provide: token, useValue: provider, multi: true };
    };
}

/**
 * @experimental
 */
class DynamicComponentFactoryResolver {
    constructor(viewContainerRef, componentFactoryResolver, host) {
        this.viewContainerRef = viewContainerRef;
        this.componentFactoryResolver = componentFactoryResolver;
        this.host = host;
    }
    resolveComponentFactory(component) {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
        return new DynamicComponentFactory(this.viewContainerRef, this.componentFactoryResolver, componentFactory, this.host);
    }
}
/**
 * @experimental
 */
class DynamicComponentFactory {
    constructor(viewContainerRef, componentFactoryResolver, componentFactory, host) {
        this.viewContainerRef = viewContainerRef;
        this.componentFactoryResolver = componentFactoryResolver;
        this.componentFactory = componentFactory;
        this.host = host;
    }
    get componentType() {
        return this.componentFactory.componentType;
    }
    get inputs() {
        return this.componentFactory.inputs;
    }
    get ngContentSelectors() {
        return this.componentFactory.ngContentSelectors;
    }
    get outputs() {
        return this.componentFactory.outputs;
    }
    get selector() {
        return this.componentFactory.selector;
    }
    get viewDefFactory() {
        return this.componentFactory.viewDefFactory;
    }
    create(injector, projectableNodes, rootSelectorOrNode, ngModule) {
        const componentRef = this.componentFactory.create(injector, projectableNodes, rootSelectorOrNode, ngModule);
        return new DynamicComponentRef(this.viewContainerRef, this.componentFactoryResolver, componentRef, this.host);
    }
}
/**
 * @experimental
 */
class DynamicComponentRef {
    constructor(viewContainerRef, componentFactoryResolver, componentRef, host) {
        this.viewContainerRef = viewContainerRef;
        this.componentFactoryResolver = componentFactoryResolver;
        this.componentRef = componentRef;
        const componentFactory = componentFactoryResolver.resolveComponentFactory(this.componentType);
        this.componentAdapterRef = new NgxComponentOutletAdapterRef({
            componentFactory,
            componentRef,
            host,
        }, viewContainerRef, componentFactoryResolver);
    }
    get changeDetectorRef() {
        return this.componentRef.changeDetectorRef;
    }
    get componentType() {
        return this.componentRef.componentType;
    }
    get hostView() {
        return this.componentRef.hostView;
    }
    get injector() {
        return this.componentRef.injector;
    }
    get instance() {
        return this.componentRef.instance;
    }
    get location() {
        return this.componentRef.location;
    }
    get _elDef() {
        return this.componentRef._elDef;
    }
    updateContext(context) {
        this.componentAdapterRef.updateContext(context);
    }
    destroy() {
        if (this._onDestroy) {
            this._onDestroy();
        }
        this.componentAdapterRef.dispose();
    }
    onDestroy(callback) {
        this._onDestroy = callback;
    }
    setInput(name, value) {
        this.componentRef.setInput(name, value);
    }
}
/**
 * @experimental
 */
class DynamicComponentAdapterBuilder {
    create(componentType, viewContainerRef, injector, projectableNodes, host, componentFactoryResolver) {
        const dynamicComponentFactoryResolver = new DynamicComponentFactoryResolver(viewContainerRef, componentFactoryResolver, host);
        const componentFactory = dynamicComponentFactoryResolver.resolveComponentFactory(componentType);
        return viewContainerRef.createComponent(componentFactory, viewContainerRef.length, injector, projectableNodes);
    }
}
/** @nocollapse */ DynamicComponentAdapterBuilder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: DynamicComponentAdapterBuilder, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ DynamicComponentAdapterBuilder.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: DynamicComponentAdapterBuilder });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.2", ngImport: i0, type: DynamicComponentAdapterBuilder, decorators: [{
            type: Injectable
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { Dynamic, DynamicComponentAdapterBuilder, DynamicComponentFactory, DynamicComponentFactoryResolver, DynamicComponentRef, NgxComponentOutletDirective, NgxComponentOutletResolvePipe, NgxdModule, NgxdProvider, NgxdResolver };
